import React, { useEffect, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Landing from "@/pages/Landing";
import { captureUTM } from "@/lib/utm";
import { fireCapi, getOrCreateFbp, getOrCreateFbc, getOrCreateExternalId, fetchClientIp } from "@/lib/pixel-events";

/* Code-split heavier routes so home loads lighter */
const Order = lazy(() => import(/* webpackChunkName: "order" */ "@/pages/Order"));
const ThankYou = lazy(() => import(/* webpackChunkName: "thankyou" */ "@/pages/ThankYou"));
const PaymentReturn = lazy(() => import(/* webpackChunkName: "payment" */ "@/pages/PaymentReturn"));

/* Static legal/info pages — lazy loaded (very low traffic, no need to bundle in main) */
const Contact = lazy(() => import(/* webpackChunkName: "legal" */ "@/pages/Contact"));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "legal" */ "@/pages/PrivacyPolicy"));
const ReturnRequest = lazy(() => import(/* webpackChunkName: "legal" */ "@/pages/ReturnRequest"));
const ShippingPolicy = lazy(() => import(/* webpackChunkName: "legal" */ "@/pages/ShippingPolicy"));
const TermsConditions = lazy(() => import(/* webpackChunkName: "legal" */ "@/pages/TermsConditions"));
const Complaint = lazy(() => import(/* webpackChunkName: "legal" */ "@/pages/Complaint"));

/* AEO — 50+ Q&A FAQ page with FAQPage JSON-LD schema */
const FAQ = lazy(() => import(/* webpackChunkName: "faq" */ "@/pages/FAQ"));

/* Hidden password-protected admin dashboard — path intentionally obfuscated */
const AdminPanel = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPanel"));

/* Preview-only sandbox for the "Prepaid Unlocked" party pop */
const PreviewParty = lazy(() => import(/* webpackChunkName: "preview-party" */ "@/pages/PreviewParty"));

const RouteFallback = () => (
  <div className="m2s-frame min-h-screen grid place-items-center">
    <div className="text-emerald-700 text-sm">Loading…</div>
  </div>
);

function App() {
  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return; // guard against StrictMode/HMR double-fire
    bootedRef.current = true;
    // Skip pixel/CAPI boot on hidden admin surface so it doesn't pollute Meta EMQ.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/portal-")) {
      return;
    }
    captureUTM();
    getOrCreateFbp();
    getOrCreateFbc();
    getOrCreateExternalId();
    // Kick off IP fetch early (IPv6-preferred) so it's cached by the time
    // ViewContent/AddToCart/Order CAPI calls fire — improves Meta EMQ.
    fetchClientIp();
    fireCapi("PageView");

    // Prefetch /order chunk during idle to make home→checkout instant
    const prefetch = () => import(/* webpackChunkName: "order" */ "@/pages/Order");
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(prefetch, { timeout: 2500 });
    } else {
      setTimeout(prefetch, 1500);
    }
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/order" element={<Order />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/payment-return" element={<PaymentReturn />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/return-request" element={<ReturnRequest />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/complaint" element={<Complaint />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/portal-a9k3m2xp" element={<AdminPanel />} />
          <Route path="/preview-party" element={<PreviewParty />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
